import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { StickerDirective } from '@usitsdasdesign/dds-ng/sticker';
import { SearchComponent as DDSSearchComponent } from '@usitsdasdesign/dds-ng/search';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  INFINITE_SCROLL_ITEMS_PER_PAGE,
  INFINITE_SCROLL_THRESHOLD,
  INFINITE_SCROLL_DEBOUNCE_TIME,
  INFINITE_SCROLL_PRELOAD_PERCENTAGE,
} from '@shared/constants/infinite-scroll.constants';

interface SearchOption {
  label: string;
  value: unknown;
}

@Component({
  standalone: false,
  selector: 'hm-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchComponent),
      multi: true,
    },
  ],
})
export class SearchComponent implements OnChanges, ControlValueAccessor {
  @Input() placeholder = '';
  @Input() options!: SearchOption[];
  @Input() optionTemplate!: TemplateRef<unknown>;
  @Input() minTermLength = 3;

  @Input() label = '';
  @Input() isRequired = false;
  @Input() errorMessage = '';

  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  @Input() optionLabelField: 'label' = 'label';

  @ViewChild('sticker') sticker!: StickerDirective;
  @ViewChild('search') search?: DDSSearchComponent;

  @Output() optionSelect = new EventEmitter<SearchOption | null>();

  public isErrorRequired = false;
  protected currentPage = 1;
  protected readonly itemsPerPage = INFINITE_SCROLL_ITEMS_PER_PAGE;
  protected readonly scrollThreshold = INFINITE_SCROLL_THRESHOLD;
  protected readonly scrollDebounce = INFINITE_SCROLL_DEBOUNCE_TIME;
  protected readonly preloadPercentage = INFINITE_SCROLL_PRELOAD_PERCENTAGE;

  private readonly termSubject = new BehaviorSubject('');
  private readonly valueSubject = new BehaviorSubject<SearchOption | null>(
    null,
  );
  private readonly optionsChange$ = new BehaviorSubject<SearchOption[]>(
    this.options,
  );
  private filteredOptions: SearchOption[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options'] && this.options.length) {
      this.optionsChange$.next(this.options);
    }
  }

  protected readonly options$ = combineLatest([
    this.valueSubject.asObservable(),
    this.termSubject.asObservable(),
    this.optionsChange$.asObservable(),
  ]).pipe(
    map(([value, term, options]) => {
      this.sticker.updPosition();

      if (value) {
        this.filteredOptions = this.filterByValue(options, value);
      } else {
        this.filteredOptions = this.filterByTerm(options, term);
      }

      this.currentPage = 1;

      return this.filteredOptions;
    }),
  );

  protected isDisabled = false;
  protected touched = false;
  private onModelChange!: (value: SearchOption | null) => void;
  private onModelTouched!: () => void;

  set value(value: SearchOption | null) {
    this.valueSubject.next(value);
  }

  get value(): SearchOption | null {
    return this.valueSubject.value;
  }

  get isOptionsAvailable(): boolean {
    return this.optionsChange$.value?.length > 0;
  }

  get term() {
    return this.termSubject.value;
  }

  writeValue(value: SearchOption | null) {
    this.value = value;
    this.termSubject.next(this.value?.[this.optionLabelField] ?? '');
  }

  registerOnChange(fn: (value: SearchOption | null) => void) {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onModelTouched = fn;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onModelTouched();
      this.touched = true;
    }
  }

  onOptionSelected(value: SearchOption | null) {
    this.markAsTouched();

    this.value = value;
    this.onModelChange(value);
    this.termSubject.next(value?.[this.optionLabelField] ?? '');
    this.isErrorRequired = false;
    this.sticker.hide();
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
    this.search?.setDisabledState(isDisabled);
  }

  onTermChanged(term: string) {
    this.markAsTouched();
    this.termSubject.next(term.trim());
    this.value = null;
    this.onModelChange(null);
    this.currentPage = 1;
  }

  onHidden() {
    if (this.term) {
      return;
    }
    if (this.isRequired) {
      this.isErrorRequired = true;
    }
    this.value = null;
    this.onModelChange(null);
  }

  onClearField() {
    this.termSubject.next('');
    this.value = null;
    this.onModelChange(null);
    this.sticker.show();
    this.currentPage = 1;
  }

  loadMore() {
    if (this.hasMoreItems) {
      this.currentPage++;
    }
  }

  get hasMoreItems(): boolean {
    return this.filteredOptions.length > this.currentPage * this.itemsPerPage;
  }

  hasMoreItemsFor(options: SearchOption[]): boolean {
    if (!options || options.length === 0) {
      return false;
    }
    const displayedItems = this.currentPage * this.itemsPerPage;
    return options.length > displayedItems;
  }

  private filterByValue(
    options: SearchOption[],
    value: SearchOption | null,
  ): SearchOption[] {
    if (value) {
      return options.filter(
        (option) =>
          option[this.optionLabelField] !== value[this.optionLabelField],
      );
    }

    return [...options];
  }

  private filterByTerm(options: SearchOption[], term: string): SearchOption[] {
    if (term && term.length >= this.minTermLength) {
      return options.filter((option) =>
        option[this.optionLabelField]
          ?.toLowerCase()
          .includes(term.toLowerCase()),
      );
    }

    return [...options];
  }
}
